/*
 * Copy as markdown (A28, spec 06 §4). The rule that decides everything here: what a person copies
 * out of teamBase is the **stored markdown**, byte for byte — not a re-serialisation of the render
 * tree, and not the visible text. Pasting one message into another conversation must produce the
 * same message.
 *
 * A multi-message copy adds a transcript head per message (author · time · permalink, each of the
 * three switchable) and quotes the body, so the result reads as a quotation rather than as an
 * anonymous block. The `text/html` flavour is produced from the same `MarkdownBody` the timeline
 * draws — the only HTML this product makes, produced on demand and never re-entering the DOM. It
 * goes onto the clipboard beside the markdown, because the other half of the request is a paste
 * into a document or a mail, which has no use for markdown source (A-11 item 19).
 *
 * The HTML is wrapped in `<div data-teambase-markdown>`: a paste back into a teamBase composer sees
 * the marker, ignores the rendered HTML and parses the `text/plain` flavour, so the round trip
 * inside the product stays the stored markdown byte for byte (EDITOR §10.1).
 */

import { createElement } from "react"
import { formatDateTime } from "../../../core/i18n/format.ts"
import { MarkdownBody } from "../../../core/render/MarkdownBody.tsx"
import type { MessageView } from "../wire/views.ts"

export interface TranscriptOptions {
	authorName: (userId: number) => string
	/** The message's address, or the empty string where the conversation has none. */
	permalink: (seq: number) => string
	includeAuthor?: boolean
	includeTime?: boolean
	includePermalink?: boolean
}

/** What one message contributes to the clipboard: its source, unchanged. */
export function messageMarkdown(msg: MessageView): string {
	return msg.markdown
}

function quote(markdown: string): string {
	return markdown
		.split("\n")
		.map((line) => (line === "" ? ">" : `> ${line}`))
		.join("\n")
}

/**
 * The plain-text flavour. One message copies as itself; a selection copies as a transcript, because
 * without the heads a run of quotes loses who said what and when.
 */
export function buildTranscript(messages: readonly MessageView[], opts: TranscriptOptions): string {
	if (messages.length === 1 && messages[0]) return messageMarkdown(messages[0])
	const includeAuthor = opts.includeAuthor ?? true
	const includeTime = opts.includeTime ?? true
	const includePermalink = opts.includePermalink ?? true

	return messages
		.map((msg) => {
			const head: string[] = []
			// A system row carries no author id, and its head is then the empty name.
			if (includeAuthor) head.push(`**${opts.authorName(msg.authorId ?? 0)}**`)
			if (includeTime) head.push(formatDateTime(msg.createdAt))
			if (includePermalink) {
				const url = opts.permalink(msg.seq)
				if (url !== "") head.push(url)
			}
			const body = quote(messageMarkdown(msg))
			return head.length > 0 ? `${head.join(" · ")}\n${body}` : body
		})
		.join("\n\n")
}

/**
 * What a forward puts into the target conversation's draft (X-A4 N11): the quoted message with its
 * attribution, and the sender's own note under it.
 */
export interface ForwardQuote {
	/** The already translated line that says whose message this is and where it came from. */
	head: string
	/** The quoted message's source, unchanged — the same bytes `copyMarkdown` puts out. */
	markdown: string
	/** What the person forwarding added; an empty note adds nothing at all. */
	note?: string
}

/**
 * A forward, as markdown. The head stands *inside* the quotation, so what is quoted carries its own
 * attribution wherever it goes next, and the note stands under it as the sender's own words.
 *
 * Nothing is sent here: this text goes into a draft, which is what lets the sender read the whole
 * thing before it leaves — the behaviour that makes forwarding out of a private channel a decision
 * somebody took rather than a leak nobody saw.
 */
export function buildForwardQuote(forward: ForwardQuote): string {
	const quoted = quote(
		forward.markdown === "" ? forward.head : `${forward.head}\n${forward.markdown}`,
	)
	const note = forward.note ?? ""
	return note === "" ? quoted : `${quoted}\n\n${note}`
}

/**
 * The marker a teamBase composer looks for on a paste: what follows was copied out of teamBase and
 * its markdown source is on the clipboard beside it.
 */
export const MARKDOWN_MARKER_ATTRIBUTE = "data-teambase-markdown"

/** The rich flavour, rendered through the timeline's own renderer. */
async function buildHtml(messages: readonly MessageView[]): Promise<string> {
	const { renderToStaticMarkup } = await import("react-dom/server")
	const body = messages
		.map((msg) =>
			renderToStaticMarkup(
				createElement(MarkdownBody, { render: msg.render, source: msg.markdown }),
			),
		)
		.join("\n")
	return `<div ${MARKDOWN_MARKER_ATTRIBUTE}>${body}</div>`
}

/**
 * Puts a selection on the clipboard in both flavours and answers whether it worked. A browser
 * without `ClipboardItem` gets plain text; one without the async clipboard gets the `copy` event
 * fallback, which is the last path that works inside a user gesture.
 */
export async function copyMessages(
	messages: readonly MessageView[],
	opts: TranscriptOptions,
): Promise<boolean> {
	if (messages.length === 0) return false
	const text = buildTranscript(messages, opts)
	// Built before the clipboard is touched: every path that can carry it wants the same string,
	// and the event path below cannot await anything once the gesture has started.
	const html = await buildHtml(messages).catch(() => "")
	try {
		if (typeof ClipboardItem === "function" && navigator.clipboard?.write && html !== "") {
			await navigator.clipboard.write([
				new ClipboardItem({
					"text/plain": new Blob([text], { type: "text/plain" }),
					"text/html": new Blob([html], { type: "text/html" }),
				}),
			])
			return true
		}
		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text)
			return true
		}
	} catch {
		// A refused clipboard permission is not an error worth throwing at the caller; fall through
		// to the event path, which needs no permission because it is the gesture itself.
	}
	return legacyCopy(text, html)
}

function legacyCopy(text: string, html: string): boolean {
	if (typeof document.execCommand !== "function") return false
	const onCopy = (event: ClipboardEvent) => {
		event.clipboardData?.setData("text/plain", text)
		if (html !== "") event.clipboardData?.setData("text/html", html)
		event.preventDefault()
	}
	document.addEventListener("copy", onCopy)
	try {
		return document.execCommand("copy")
	} finally {
		document.removeEventListener("copy", onCopy)
	}
}
